import React from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink, Quote } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { REVIEWS, REVIEWS_AVG, REVIEWS_COUNT, GOOGLE_REVIEWS_URL } from "../lib/data";

const Rating = ({ value }) => (
  <div className="flex items-center gap-0.5" aria-label={`Rating ${value} of 5`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < value ? "fill-yellow-400 text-yellow-400" : "text-slate-200"
        }`}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

export const Reviews = () => {
  const { lang, t } = useLanguage();
  const avg = REVIEWS_AVG;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 md:py-32 bg-sky-50/60 overflow-hidden"
    >
      <div className="absolute inset-x-0 -top-10 h-20 bg-gradient-to-b from-white to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span
              data-testid="reviews-eyebrow"
              className="inline-flex text-[11px] tracking-[0.2em] uppercase text-sky-700 font-semibold rounded-full bg-white px-4 py-1.5 border border-sky-100"
            >
              {t.reviews.eyebrow}
            </span>
            <h2
              data-testid="reviews-title"
              className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-slate-900"
            >
              {t.reviews.title}
            </h2>
            <p className="mt-4 text-slate-600">{t.reviews.subtitle}</p>
          </div>

          <div
            className="flex items-center gap-4 bg-white rounded-2xl border border-sky-100 px-5 py-4 shadow-sm"
            data-testid="reviews-summary"
          >
            <div className="text-4xl font-heading font-semibold text-slate-900">
              {avg.toFixed(1)}
            </div>
            <div>
              <Rating value={Math.round(avg)} />
              <div className="text-xs text-slate-500 mt-1">
                {REVIEWS_COUNT} Google reviews
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="reviews-grid">
          {REVIEWS.map((r, i) => (
            <motion.article
              key={i}
              data-testid={`review-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.06 }}
              className={`${i >= 3 ? "hidden md:block" : ""} relative rounded-3xl bg-white p-6 md:p-7 border border-sky-100 shadow-[0_6px_24px_rgba(2,132,199,0.06)] hover:shadow-[0_14px_40px_rgba(2,132,199,0.12)] hover:-translate-y-1 transition-all duration-500`}
            >
              <Quote className="absolute top-5 right-5 w-7 h-7 text-yellow-300" strokeWidth={2} />
              <Rating value={r.rating} />
              <p className="mt-4 text-slate-700 leading-relaxed text-[15px]">
                {r.text[lang] || r.text.cz}
              </p>
              <div className="mt-6 flex items-center gap-3 pt-5 border-t border-sky-50">
                <div className="w-10 h-10 grid place-items-center rounded-full bg-gradient-to-br from-sky-400 to-sky-600 text-white font-semibold text-sm">
                  {r.initials}
                </div>
                <div className="leading-tight">
                  <div className="text-slate-900 font-semibold text-sm">
                    {r.name}
                  </div>
                  <div className="text-xs text-slate-400">
                    {typeof r.date === "string" ? r.date : r.date[lang] || r.date.cz}
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-link"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-sky-100 hover:border-sky-300 px-6 py-3 text-sm font-semibold text-slate-800 hover:-translate-y-0.5 transition-all shadow-sm"
          >
            {t.reviews.readMore}
            <ExternalLink className="w-4 h-4" strokeWidth={2.5} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
